import React from 'react';
import { useIntl } from 'react-intl';
import { useLocalizationContext } from '../LocalizationProvider';
import { useEntityContext } from '../../../util/entity-provider/context';
import toDateTime from '../util/toDateTime';
import { getLuxonDateFormat } from './util/get-luxon-date-format';

const shortDate = ({
  date,
  formatDate,
  timeZone,
  withDayOfWeek = false,
  useEntityDateFormat,
  companyDateFormat,
  locale,
}) => {
  if (!date) return '';

  const dateTime = toDateTime(date, timeZone);
  if (useEntityDateFormat && companyDateFormat) {
    return getLuxonDateFormat({ companyDateFormat, dateTime, withDayOfWeek, locale });
  }

  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
    weekday: withDayOfWeek ? 'short' : undefined,
  };
  return formatDate(dateTime, options);
};

export const ShortDate = ({ date, withDayOfWeek = false, useEntityDateFormat = false }) => {
  const { formatDate, locale } = useIntl();
  const { timeZone } = useLocalizationContext();
  const { entitySetup } = useEntityContext();
  const companyDateFormat = entitySetup?.dateTimeSettings?.dateFormat;

  return shortDate({ date, formatDate, timeZone, withDayOfWeek, useEntityDateFormat, companyDateFormat, locale });
};

export const useShortDate = ({ overrideTimeZone = undefined, useEntityDateFormat = false } = {}) => {
  const { formatDate, locale } = useIntl();
  const { timeZone } = useLocalizationContext();
  const { entitySetup } = useEntityContext();
  const companyDateFormat = entitySetup?.dateTimeSettings?.dateFormat;

  const getShortDate = React.useCallback(
    (date, withDayOfWeek = false) => {
      return shortDate({
        date,
        formatDate,
        timeZone: overrideTimeZone ?? timeZone,
        withDayOfWeek,
        useEntityDateFormat,
        companyDateFormat,
        locale,
      });
    },
    [formatDate, overrideTimeZone, timeZone, useEntityDateFormat, companyDateFormat, locale]
  );

  return { getShortDate, ShortDate };
};
